import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  Directive,
  Input,
  TemplateRef,
  TrackByFunction,
  ViewChild,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatFormField, MatFormFieldModule } from '@angular/material/form-field';
import { MatSelect, MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { UntilDestroy } from '@ngneat/until-destroy';
import { DirtyInvalidErrorStateMatcher } from '@y42/angular/forms';
import { connectState, InputObservable } from '@y42/angular/util';
import { IconModule } from '@y42/core/icon';
import { ReactiveLifecycleHooksDirective } from '@y42/core/reactive-lifecycle-hooks';
import { combineLatest, map, Observable, switchMap } from 'rxjs';
import { selectFirstErrorMessage } from '../../util/error-messages';

const errorStateMatcher = new DirtyInvalidErrorStateMatcher();

export interface SelectOption {
  label: string;
  value: string;
  $implicit: string;
}

export const trackBySelectItemValue: TrackByFunction<SelectOption> = (index, item) => item.value ?? index;

export type SelectOptionValueFn<T extends object = SelectOption> = (item: T) => unknown;
export type SelectOptionLabelFn<T extends object = SelectOption> = (item: T) => string;

export interface SelectOptionType<T extends object = SelectOption> {
  $implicit: T;
}

@Directive({
  selector: 'ng-template[y42SelectOption]',
  standalone: true,
})
export class SelectOptionDirective<T extends object = SelectOption> {
  constructor(public readonly templateRef: TemplateRef<T | null>) {}

  @Input() y42SelectOption!: T;

  static ngTemplateContextGuard<T extends object>(dir: SelectOptionDirective<T>, ctx: unknown): ctx is T {
    return true;
  }
}

const SELECT_IMPORTS: Component['imports'] = [
  CommonModule,
  ReactiveFormsModule,
  MatFormFieldModule,
  MatSelectModule,
  MatTooltipModule,
  IconModule,
];

@UntilDestroy()
@Directive()
export class _SelectComponent<
  Option extends object = SelectOption,
  Value = unknown,
> extends ReactiveLifecycleHooksDirective {
  @ViewChild(MatSelect) matSelect!: MatSelect;

  @Input() @InputObservable() label?: string | TemplateRef<unknown> | null;
  private readonly label$!: Observable<string | TemplateRef<unknown> | null>;

  @Input() @InputObservable() options?: Option[] | null;
  private readonly options$!: Observable<Option[] | null | undefined>;

  @Input() @InputObservable() optionValue?: keyof Option | SelectOptionValueFn<Option>;
  private readonly optionValue$!: Observable<keyof Option | SelectOptionValueFn<Option> | undefined>;

  @Input() @InputObservable() optionLabel?: keyof Option | SelectOptionLabelFn<Option>;
  private readonly optionLabel$!: Observable<keyof Option | SelectOptionLabelFn<Option> | undefined>;

  @ContentChild(SelectOptionDirective) optionTmpl?: SelectOptionDirective;

  @Input() formCtrl!: FormControl<Value>;
  @Input() hideErrors = false;
  @Input() errorMessages?: Record<string, string>;
  @Input() required = false;
  @Input() color?: MatFormField['color'];
  @Input() tooltipHint?: string;
  @Input() trackBy: TrackByFunction<Option & SelectOption> = trackBySelectItemValue;

  // MatSelect properties
  @Input() compareWith?: MatSelect['compareWith'];
  @Input() panelClass?: MatSelect['panelClass'];
  @Input() placeholder?: MatSelect['placeholder'];
  @Input() hideSingleSelectionIndicator?: MatSelect['hideSingleSelectionIndicator'] = true;
  // ---

  readonly errorStateMatcher = errorStateMatcher;

  readonly state = connectState(this, {
    labelTemplate: this.label$.pipe(
      map((label) => label instanceof TemplateRef),
      map((isTemplatRef) => (isTemplatRef ? (this.label as TemplateRef<unknown>) : null)),
    ),
    errorMessage: this.ngAfterViewInit$.pipe(
      map(() => this.formCtrl),
      switchMap((ctrl) => selectFirstErrorMessage(ctrl, this.errorMessages)),
    ),
    options: combineLatest([this.options$, this.optionValue$, this.optionLabel$]).pipe(
      map(([options, optionValue, optionLabel]) => {
        if (!options) {
          return [];
        }

        return options.map((option) => {
          const label =
            typeof optionLabel === 'function'
              ? optionLabel(option)
              : optionLabel
              ? option[optionLabel]
              : 'Missing "optionLabel"';
          const value =
            typeof optionValue === 'function' ? optionValue(option) : optionValue ? option[optionValue] : option;

          return {
            label,
            value,
            option,
          };
        });
      }),
    ),
  });

  focus() {
    this.matSelect.focus();
  }
}

@UntilDestroy()
@Component({
  selector: 'y42-single-select',
  standalone: true,
  imports: SELECT_IMPORTS,
  templateUrl: './select.component.html',
  styleUrls: ['./select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class _SingleSelectComponent<Option extends object = SelectOption, Value = object> extends _SelectComponent<
  Option,
  Value
> {
  readonly _multiple = false;
}

@UntilDestroy()
@Component({
  selector: 'y42-multi-select',
  standalone: true,
  imports: SELECT_IMPORTS,
  templateUrl: './select.component.html',
  styleUrls: ['./select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class _MultiSelectComponent<
  Option extends object = SelectOption,
  Value extends unknown[] = unknown[],
> extends _SelectComponent<Option, Value> {
  readonly _multiple = true;
}

export const SingleSelectComponent = [_SingleSelectComponent, SelectOptionDirective];
export const MultiSelectComponent = [_MultiSelectComponent, SelectOptionDirective];
